// Building blocks shared by the Settings sections.
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Check, ImagePlus, Loader2, Palette, RotateCcw, Upload, X } from 'lucide-react'
import { contrastRatio, isHex, isLowContrast } from './settingsModel'
import { indicatorStyle, useIndicator, usePresence, useReducedMotion, useSwapIn } from './settingsMotion'

// Deep, sidebar-friendly shades. Deduplicated because the list is edited by hand.
const QUICK_COLORS = [...new Set([
  '#450a0a', '#7f1d1d', '#991b1b', '#b91c1c', '#8b0000', '#6b0f0f', '#800020', '#722f37', '#6b1010', '#5c1a1a',
  '#5c2d0e', '#7c2d12', '#9a3412', '#92400e', '#78350f', '#6d3a1a', '#8b4513', '#704214', '#5e3a1a', '#4a2c17',
  '#713f12', '#854d0e', '#a16207', '#7a6520', '#665c1e', '#556b2f', '#4d5e2f', '#3d4f1e', '#5a5a00', '#6b6b00',
  '#052e16', '#14532d', '#166534', '#1b4332', '#1a4731', '#004d40', '#00695c', '#1e3932', '#2d3a2e', '#1a3c34',
  '#064e3b', '#115e59', '#134e4a', '#0b3948', '#0d4f4f', '#003b46', '#044259', '#0c3547', '#164e63', '#155e75',
  '#0a1628', '#0f1f3d', '#00264d', '#003366', '#0c2340', '#1a2744', '#1e2a3b', '#1e3a5f', '#003d73', '#0a3d62',
  '#1b2838', '#1a365d', '#1e40af', '#1d4ed8', '#0e4d92', '#1e3a8a', '#172554', '#1e1b4b', '#0c1445', '#1e1040',
  '#2d1b69', '#312e81', '#3730a3', '#4c1d95', '#4338ca', '#4f46e5', '#3b0764', '#2e1065', '#1e0a4e', '#4a044e',
  '#581c87', '#6b21a8', '#701a75', '#86198f', '#831843', '#9d174d', '#5b1a3a', '#4a0e1a', '#3b0d2e', '#500724',
  '#881337', '#9f1239', '#be185d', '#a21caf', '#7e22ce', '#6d28d9', '#5b21b6', '#0f172a', '#111827', '#18181b',
  '#1e293b', '#1f2937', '#27272a', '#292524', '#1c1917', '#262626', '#171717',
])]

const CHECKER = 'repeating-conic-gradient(var(--chk-a) 0% 25%, var(--chk-b) 0% 50%) 50% / 12px 12px'
const CHECKER_VARS = '[--chk-a:#eceef1] [--chk-b:#ffffff] dark:[--chk-a:#2a2a2a] dark:[--chk-b:#1f1f1f]'

export function Panel({ title, description, actions, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]
                         dark:border-gray-800 dark:bg-gray-900 ${className}`}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 px-5 pt-4">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>}
            {description && <p className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={`px-5 pb-5 ${title || actions ? 'pt-4' : 'pt-5'}`}>{children}</div>
    </section>
  )
}

export function FieldError({ children }) {
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600 animate-panel-in dark:text-red-400">
      <AlertTriangle className="h-3 w-3 shrink-0" /> {children}
    </p>
  )
}

// The character counter only shows while the field is focused or nearly full.
export function Field({ label, htmlFor, hint, error, count, max, children, className = '' }) {
  const [focused, setFocused] = useState(false)
  const nearLimit = max && count > max * 0.9
  return (
    <div className={className} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className="text-[13px] font-medium text-gray-700 dark:text-gray-300">{label}</label>
        {max ? (
          <span className={`text-[11px] tabular-nums transition-opacity duration-200
                            ${focused || nearLimit ? 'opacity-100' : 'opacity-0'}
                            ${nearLimit ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
            {count}/{max}
          </span>
        ) : null}
      </div>
      {children}
      {error ? <FieldError>{error}</FieldError> : hint ? (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  )
}

export function useDismiss(open, onClose, ref) {
  useEffect(() => {
    if (!open) return undefined
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, ref])
}

const COLOR_SIZES = {
  lg: { swatch: 'h-10 w-10 rounded-lg', input: 'h-10 w-28 text-sm', button: 'h-10 w-10' },
  md: { swatch: 'h-9 w-9 rounded-lg', input: 'h-9 w-[6.5rem] text-[13px]', button: 'h-9 w-9' },
  sm: { swatch: 'h-8 w-8 rounded-md', input: 'h-8 w-24 text-[13px]', button: 'h-8 w-8' },
}

// Swatch, hex input and quick palette. Partial hex is allowed while typing;
// the page blocks saving until it's complete.
export function ColorField({ id, value, onChange, disabled, invalid, size = 'md', align = 'left', fallback = '#1e2a3b', ariaLabel }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const buttonRef = useRef(null)
  const { mounted, shown } = usePresence(open, 160)
  useDismiss(open, () => setOpen(false), wrapRef)

  const s = COLOR_SIZES[size]
  const safe = isHex(value) ? value : fallback

  return (
    <div ref={wrapRef} className="relative flex items-center gap-1.5">
      <label className={`relative block shrink-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="color"
          value={safe}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          aria-label={ariaLabel ? `${ariaLabel} colour picker` : 'Colour picker'}
          className="absolute inset-0 h-full w-full cursor-[inherit] opacity-0"
        />
        <span
          className={`${s.swatch} block shadow-inner ring-1 ring-black/10 transition-[background-color,transform] duration-300
                      dark:ring-white/15 ${disabled ? '' : 'hover:scale-105'}`}
          style={{ backgroundColor: safe }}
        />
      </label>
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        maxLength={7}
        spellCheck={false}
        autoComplete="off"
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel ? `${ariaLabel} hex` : undefined}
        onChange={e => {
          let v = e.target.value.trim()
          if (v && !v.startsWith('#')) v = `#${v}`
          if (/^#[0-9a-fA-F]{0,6}$/.test(v) || v === '') onChange(v || '#')
        }}
        className={`${s.input} rounded-lg border bg-white px-2.5 font-mono uppercase text-gray-900 transition-colors
                    focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-800 dark:text-gray-100
                    ${invalid
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 dark:border-red-700'
                      : 'border-gray-200 focus:border-navy-500 focus:ring-navy-500/20 dark:border-gray-700'}`}
      />
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-label="Quick palette"
        title="Quick palette"
        className={`${s.button} grid shrink-0 place-items-center rounded-lg border border-gray-200 text-gray-500 transition-colors
                    hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50
                    dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200
                    ${open ? 'bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-200' : ''}`}
      >
        <Palette className="h-4 w-4" />
      </button>

      {mounted && (
        <div
          className={`absolute top-full z-40 mt-2 w-[17.5rem] rounded-xl border border-gray-200 bg-white p-2.5 shadow-xl
                      transition-[opacity,transform] duration-150 ease-out dark:border-gray-700 dark:bg-gray-800
                      ${align === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'}
                      ${shown ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        >
          <div className="grid max-h-44 grid-cols-10 gap-1 overflow-y-auto pr-0.5">
            {QUICK_COLORS.map(color => {
              const active = value?.toLowerCase() === color
              return (
                <button
                  key={color}
                  type="button"
                  title={color.toUpperCase()}
                  aria-label={`Use ${color}`}
                  onClick={() => { onChange(color); setOpen(false); buttonRef.current?.focus() }}
                  className={`relative h-6 w-6 rounded-md transition-transform duration-150 hover:z-10 hover:scale-110
                              ${active ? 'ring-2 ring-navy-500 ring-offset-1 dark:ring-offset-gray-800' : 'ring-1 ring-black/10 dark:ring-white/10'}`}
                  style={{ backgroundColor: color }}
                >
                  {active && <Check className="absolute inset-0 m-auto h-3 w-3 text-white" strokeWidth={3} />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export function LowContrast({ fg, bg }) {
  const ratio = contrastRatio(fg, bg)
  const show = isLowContrast(ratio)
  return (
    <span
      aria-hidden={!show}
      title={show ? `${ratio.toFixed(1)}:1 contrast. Aim for 4.5:1.` : undefined}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700
                  ring-1 ring-inset ring-amber-200 transition-[opacity,transform] duration-200
                  dark:bg-amber-900/25 dark:text-amber-300 dark:ring-amber-800/60
                  ${show ? 'scale-100 opacity-100' : 'pointer-events-none scale-90 opacity-0'}`}
    >
      <AlertTriangle className="h-3 w-3" /> Low contrast
    </span>
  )
}

export function ImageDrop({ label, hint, url, onFile, disabled, busy, compact = false, previewClassName = 'max-h-14 max-w-[70%]' }) {
  const inputRef = useRef(null)
  const [over, setOver] = useState(false)
  const inputId = useId()
  const pick = file => { if (file && !disabled) onFile(file) }
  const open = () => { if (!disabled) inputRef.current?.click() }

  const dropProps = {
    onDragOver: e => { if (disabled) return; e.preventDefault(); setOver(true) },
    onDragLeave: () => setOver(false),
    onDrop: e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]) },
  }

  const input = (
    <input
      id={inputId}
      ref={inputRef}
      type="file"
      accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
      className="hidden"
      disabled={disabled}
      onChange={e => { pick(e.target.files?.[0]); e.target.value = '' }}
    />
  )

  if (compact) {
    return (
      <div className="flex items-center gap-3" {...dropProps}>
        <button
          type="button"
          onClick={open}
          disabled={disabled}
          aria-label={url ? `Replace ${label.toLowerCase()}` : `Upload ${label.toLowerCase()}`}
          className={`group relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl border transition-[border-color,transform]
                      duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${CHECKER_VARS}
                      ${over ? 'scale-105 border-navy-500' : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'}`}
          style={url ? { background: CHECKER } : undefined}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            : url ? <img key={url} src={url} alt="" className="max-h-10 max-w-10 object-contain animate-panel-in" />
              : <ImagePlus className="h-5 w-5 text-gray-400 transition-transform group-hover:scale-110" />}
        </button>
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-gray-700 dark:text-gray-300">{label}</p>
          <button
            type="button"
            onClick={open}
            disabled={disabled || busy}
            className="text-[13px] font-medium text-navy-600 hover:underline disabled:opacity-50 disabled:no-underline dark:text-navy-300"
          >
            {busy ? 'Uploading…' : url ? 'Replace' : 'Upload'}
          </button>
          {hint && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{hint}</span>}
        </div>
        {input}
      </div>
    )
  }

  return (
    <div>
      <label htmlFor={inputId} className="mb-1.5 block text-[13px] font-medium text-gray-700 dark:text-gray-300">{label}</label>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={url ? `Replace ${label.toLowerCase()}` : `Upload ${label.toLowerCase()}`}
        aria-disabled={disabled || undefined}
        onClick={open}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } }}
        {...dropProps}
        className={`group relative h-28 overflow-hidden rounded-xl border transition-[border-color,background-color,transform] duration-200
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 ${CHECKER_VARS}
                    ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                    ${url ? 'border-gray-200 dark:border-gray-700' : 'border-dashed border-gray-300 dark:border-gray-700'}
                    ${over ? 'scale-[1.015] border-navy-500 bg-navy-50/60 dark:bg-gray-800' : !url ? 'hover:border-gray-400 hover:bg-gray-50/70 dark:hover:border-gray-600 dark:hover:bg-gray-800/40' : ''}`}
      >
        {url ? (
          <>
            <div className="absolute inset-0 grid place-items-center" style={{ background: CHECKER }}>
              <img key={url} src={url} alt={`${label} preview`} className={`${previewClassName} object-contain animate-panel-in`} />
            </div>
            <div className="absolute inset-0 grid place-items-center bg-gray-900/55 opacity-0 transition-opacity duration-200
                            group-hover:opacity-100 group-focus-visible:opacity-100">
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-white">
                <Upload className="h-4 w-4" /> {over ? 'Drop to replace' : 'Replace'}
              </span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <span className="flex flex-col items-center gap-1.5">
              <Upload className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${over ? '-translate-y-1' : 'group-hover:-translate-y-0.5'}`} />
              <span className="text-[13px] font-medium text-gray-600 dark:text-gray-300">
                {over ? 'Drop to upload' : `Upload ${label.toLowerCase()}`}
              </span>
            </span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-white/70 animate-panel-in dark:bg-gray-900/70">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        )}
        {input}
      </div>
      {hint && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  )
}

export function ResetButton({ onClick, disabled, visible = true, label = 'Reset to default' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !visible}
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      title={label}
      aria-label={label}
      className={`grid h-7 w-7 place-items-center rounded-md text-gray-400 transition-[opacity,transform,color,background-color] duration-200
                  hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed dark:hover:bg-gray-800 dark:hover:text-gray-200
                  ${visible ? 'scale-100 opacity-100' : 'pointer-events-none scale-75 opacity-0'}`}
    >
      <RotateCcw className="h-3.5 w-3.5" />
    </button>
  )
}

// Height-animated disclosure. Closed content is inert so it can't be tabbed into.
export function Collapse({ open, children }) {
  return (
    <div
      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none
                  ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      inert={open ? undefined : ''}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  )
}

// Segmented control with a sliding thumb.
export function Segmented({ options, value, onChange, disabled, ariaLabel, size = 'md' }) {
  const ref = useRef(null)
  const box = useIndicator(ref, value)
  const reduced = useReducedMotion()
  const pad = size === 'sm' ? 'h-7 px-2' : 'h-8 px-3.5'

  return (
    <div ref={ref} role="radiogroup" aria-label={ariaLabel} className="relative inline-flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
      <span
        aria-hidden="true"
        className="absolute bottom-0.5 left-0 top-0.5 rounded-md bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-700 dark:ring-white/5"
        style={indicatorStyle(box, 'x', reduced)}
      />
      {options.map(option => {
        const on = option.value === value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            data-key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={option.ariaLabel}
            title={option.ariaLabel}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`relative z-[1] inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors duration-200
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 disabled:cursor-not-allowed disabled:opacity-60 ${pad}
                        ${on ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function ConfirmDialog({
  open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', busy, onConfirm, onCancel, icon: Icon = AlertTriangle,
}) {
  const { mounted, shown } = usePresence(open, 200)
  const cancelRef = useRef(null)
  const returnFocus = useRef(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return undefined
    returnFocus.current = document.activeElement
    const onKey = e => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, busy, onCancel])

  useEffect(() => {
    if (shown) cancelRef.current?.focus()
    else if (!open) returnFocus.current?.focus?.()
  }, [shown, open])

  if (!mounted) return null
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 bg-gray-950/40 backdrop-blur-[2px] transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => !busy && onCancel()}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl transition-[opacity,transform] duration-200 ease-out
                    dark:border-gray-800 dark:bg-gray-900 ${shown ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.98] opacity-0'}`}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          aria-label="Close"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400">
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 pr-6">
            <h3 id={titleId} className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <div className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{body}</div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button ref={cancelRef} type="button" onClick={onCancel} disabled={busy} className="btn btn-secondary btn-sm">
            {cancelLabel}
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className="btn btn-danger btn-sm gap-1.5 disabled:opacity-60">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// Dialog asking for one line of text, such as a theme name. `validate` returns an error or null.
export function PromptDialog({
  open, title, description, label, initialValue = '', placeholder, maxLength, confirmLabel = 'Save', busy, validate, onConfirm, onCancel, children,
}) {
  const { mounted, shown } = usePresence(open, 200)
  const inputRef = useRef(null)
  const returnFocus = useRef(null)
  const titleId = useId()
  const inputId = useId()
  const [value, setValue] = useState(initialValue)
  const [touched, setTouched] = useState(false)

  // Seed only when opening, so a parent re-render doesn't wipe what was typed.
  useEffect(() => {
    if (!open) return
    setValue(initialValue)
    setTouched(false)
    returnFocus.current = document.activeElement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, busy, onCancel])

  useEffect(() => {
    if (shown) { inputRef.current?.focus(); inputRef.current?.select() }
    else if (!open) returnFocus.current?.focus?.()
  }, [shown, open])

  const error = validate ? validate(value) : null
  const submit = e => {
    e.preventDefault()
    setTouched(true)
    if (!error) onConfirm(value.trim())
  }

  if (!mounted) return null
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 bg-gray-950/40 backdrop-blur-[2px] transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => !busy && onCancel()}
      />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={submit}
        className={`relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl transition-[opacity,transform] duration-200 ease-out
                    dark:border-gray-800 dark:bg-gray-900 ${shown ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.98] opacity-0'}`}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          aria-label="Close"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 id={titleId} className="pr-8 text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor={inputId} className="mb-1.5 block text-[13px] font-medium text-gray-700 dark:text-gray-300">{label}</label>
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              className={`form-input ${touched && error ? 'form-input-error' : ''}`}
              value={value}
              onChange={e => { setValue(e.target.value); setTouched(true) }}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={busy}
              aria-invalid={Boolean(touched && error)}
            />
            {touched && error && <FieldError>{error}</FieldError>}
          </div>
          {children}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} className="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" disabled={busy || (touched && Boolean(error))} className="btn btn-primary btn-sm gap-1.5 disabled:opacity-60">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '')

// Floating save bar. States: idle, dirty, error, saving.
export function SaveBar({ state, errorCount, onSave, onDiscard, onShowError }) {
  const open = state !== 'idle'
  // Keep showing the last visible state while the bar slides away.
  const [shownState, setShownState] = useState(state === 'idle' ? 'dirty' : state)
  useEffect(() => { if (state !== 'idle') setShownState(state) }, [state])
  const innerRef = useRef(null)
  useSwapIn(innerRef, shownState, { y: 4, duration: 240 })

  const quiet = 'rounded-lg px-3 py-1.5 text-[13px] font-medium text-white/75 transition-colors hover:bg-white/10 hover:text-white'
  const solid = 'inline-flex items-center gap-2 rounded-lg bg-white px-3.5 py-1.5 text-[13px] font-semibold text-gray-900 transition-colors hover:bg-gray-100'

  return (
    <div
      className={`pointer-events-none sticky bottom-5 z-30 mt-8 flex justify-center transition-[opacity,transform,visibility]
                  motion-reduce:transition-none ${open ? 'visible translate-y-0 opacity-100 duration-500' : 'invisible translate-y-6 opacity-0 duration-200'}`}
      style={{ transitionTimingFunction: open ? 'cubic-bezier(0.34, 1.4, 0.64, 1)' : 'ease-in' }}
    >
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto flex items-center rounded-2xl bg-gray-900 py-1.5 pl-4 pr-1.5 text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.45)]
                   ring-1 ring-white/10 dark:bg-gray-800"
      >
        <div ref={innerRef} className="flex items-center gap-2">
          {shownState === 'saving' && (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-white/70" />
              <span className="py-1.5 pr-3 text-[13px] font-medium">Saving…</span>
            </>
          )}
          {shownState === 'error' && (
            <>
              <span className="h-2 w-2 rounded-full bg-red-400" />
              <span className="mr-2 text-[13px] font-medium">
                {errorCount === 1 ? '1 field to fix' : `${errorCount} fields to fix`}
              </span>
              <button type="button" onClick={onDiscard} className={quiet}>Discard</button>
              <button type="button" onClick={onShowError} className={solid}>Show</button>
            </>
          )}
          {shownState === 'dirty' && (
            <>
              <span className="h-2 w-2 rounded-full bg-amber-400" />
              <span className="mr-2 text-[13px] font-medium">Unsaved changes</span>
              <button type="button" onClick={onDiscard} className={quiet}>Discard</button>
              <button type="button" onClick={onSave} className={solid}>
                Save
                <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1 font-sans text-[10px] font-medium text-gray-500 md:inline">
                  {IS_MAC ? '⌘S' : 'Ctrl S'}
                </kbd>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
